import * as React from "react";

export interface ContentEditableProps extends React.Props<ContentEditable> {
    children?: React.ReactElement<any>;
    value: string;
    maxLength: number;
    onChange: (textContent: string) => void;
}

interface ContentEditableState {
    editing: boolean;
    selectionOffset: number;
}

/** Converts a string to an array of char codes */
function charCodes(str: string) {
    let codes: number[] = [];
    for (let c of str) {
        codes.push(c.charCodeAt(0));
    }
    return codes;
};

function isCharacterKeyPress(evt: KeyboardEvent) {
    if (typeof evt.which === "undefined") {
        // This is IE, which only fires keypress events for printable keys
        return true;
    } else if (typeof evt.which === "number" && evt.which > 0) {
        // In other browsers except old versions of WebKit, evt.which is
        // only greater than zero if the keypress is a printable key.
        // We need to filter out backspace and ctrl/alt/meta key combinations
        return !evt.ctrlKey && !evt.metaKey && !evt.altKey && evt.which !== 8;
    }
    return false;
}

export default class ContentEditable extends React.Component<ContentEditableProps, ContentEditableState> {

    rootEl: HTMLElement;

    constructor(props?: ContentEditableProps, context?: any) {
        super(props, context);

        this.state = {
            editing : false,
            selectionOffset: 0
        };
    }

    render() {
        return React.cloneElement(this.props.children, {
            ref: (el: HTMLElement) => {
                const originalRef = this.props.children &&
                    (this.props.children as any).ref;

                if (typeof originalRef === 'function') {
                    originalRef(el);
                }

                this.rootEl = el;
            },
            contentEditable: this.state.editing,
            onClick: () => this.onButtonClick(),
            onKeyDown: this.onKeyDown,
            onKeyUp: this.onKeyUp,
            onKeyPress: this.onKeyPress,
            onPaste: this.onPaste
        });
    }

    componentDidMount() {
        this.setCurrentValue(this.props.value.toString());
    }

    componentDidUpdate(prevProps: ContentEditableProps, prevState: ContentEditableState) {
        if (prevState.editing === false && this.state.editing === true) {
            this.setCaretPosition(this.state.selectionOffset);
            this.getRootEl().focus();
        }

        if ((this.getRootEl() !== document.activeElement) && prevProps.value !== this.props.value) {
            if (this.props.value === "") {
                this.setCurrentValue(" ");
            } else {
                this.setCurrentValue(this.props.value);
            }
        }
    }

    getCurrentValue() {
        return this.getRootEl().textContent;
    }

    private setCurrentValue(val: string) {
        this.getRootEl().textContent = val;
    }

    private getRootEl() {
        return this.rootEl;
    }

    private onChange() {
        let text = this.getCurrentValue();

        if (text === "") {
            this.setCurrentValue(" ");
        }

        if (text !== this.props.value) {
            this.props.onChange(text);
        }
    };

    private onKeyDown = (e: React.KeyboardEvent<{}>) => {
        // Prevent styling with bold/italic/underline (CTRL+B/I/U)
        if (e.metaKey || e.ctrlKey) {
            if (charCodes("BIU").indexOf(e.keyCode) !== -1) {
                e.preventDefault();
                e.stopPropagation();
            }
        } else if (e.keyCode === 13 /*ENTER*/
                || e.keyCode === 108 /*NUMPAD_ENTER*/
                || e.keyCode === 27 /*ESCAPE*/) {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    private onKeyUp = (e: React.KeyboardEvent<{}>) => {
        this.onChange();
    };

    private onKeyPress = (e: React.KeyboardEvent<{}>) => {
        if (isCharacterKeyPress(e.nativeEvent as KeyboardEvent)) {
            let charsToDeleteCount = this.getSelectedCharCount();
            if (this.getCurrentValue().length - charsToDeleteCount >= this.props.maxLength) {
                e.preventDefault();
            }
        }
    };

    private onPaste = (e: React.ClipboardEvent<{}>) => {
        e.preventDefault();
        let data: string;
        try {
            data = e.clipboardData.getData('text/plain');
        } catch (err) {
            //IE luv
            data = e.clipboardData.getData('text');
        }

        // trim to maxlength
        let newTextLength = this.getCurrentValue().length - this.getSelectedCharCount();
        let totalLength = newTextLength + data.length;
        let newTotalLength = Math.min(totalLength, this.props.maxLength);
        data = data.substr(0, newTotalLength - newTextLength);

        if (data.length) {
            this.pasteText(data);
            this.onChange();
        }
    };

    private pasteText(data: string) {
        let selection = window.getSelection();
        let range = selection.getRangeAt(0);
        range.deleteContents();
        let fragment = range.createContextualFragment('');
        fragment.textContent = data;
        let replacementEnd = fragment.lastChild;
        range.insertNode(fragment);
        // Set cursor at the end of the replaced content, just like browsers do.
        range.setStartAfter(replacementEnd);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
    }

    private getSelectedCharCount() {
        let selectionRange = window.getSelection().getRangeAt(0);
        return selectionRange.cloneContents().textContent.length;
    }

    private setCaretPosition(offset: number) {
        let textEl = this.getRootEl();
        //TODO: move selection logic to separate file
        let range = document.createRange();
        let sel = window.getSelection();
        if (offset < String(textEl.childNodes[0]).length) {
            range.setStart(textEl.childNodes[0] || textEl, offset);
        }
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
    }

    private onButtonClick() {
        if (!this.state.editing) {
            this.setState({
                editing: true
            } as ContentEditableState);
        }

        //TODO: extract text selection logic to separate helper
        let selectionOffset = window.getSelection().anchorOffset;

        this.setState({
            selectionOffset
        } as ContentEditableState);
    }
}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/components/ContentEditable.tsx
 **/